import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, MoreHorizontal } from 'lucide-react'
import {
  Button, Input, Label, Card, CardContent, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../ui/dropdown-menu'
import { Textarea } from '../../ui/textarea'
import { getPromociones, createPromocion, actualizarPromocion, desactivarPromocion, getPlanes, getConceptos } from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { formatMoney } from '../../../utils/helpers'
import { porcentajeHermano } from '../../../lib/services/descuentoPromociones'

const TIPO_OPTIONS = [
  { value: 'hermano', label: 'Hermanos' },
  { value: 'beca', label: 'Beca' },
  { value: 'cortesia', label: 'Cortesia' },
  { value: 'otro', label: 'Otro' },
]

const initialForm = {
  nombre: '', tipo: 'otro',
  porcentaje: '', monto_fijo: '', descripcion: '',
  config_hijo_2: '', config_hijo_3: '', config_hijo_4: '',
  plan_id: '', concepto_id: '',
}

const PromocionesTab = ({ temporadaId, temporadaSelector = null }) => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const guard = useUnsavedGuard({ onClose: () => setFormOpen(false) })
  const [promociones, setPromociones] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(initialForm)
  const [saving, setSaving] = useState(false)

  const [planes, setPlanes] = useState([])
  const [conceptos, setConceptos] = useState([])


  const load = useCallback(async () => {
    setLoading(true)
    const [promoRes, planesRes, conceptosRes] = await Promise.all([
      getPromociones({ temporadaId }),
      getPlanes({ temporadaId }),
      getConceptos(),
    ])
    setPromociones(promoRes.data || [])
    setPlanes(planesRes.data || [])
    setConceptos((conceptosRes.data || []).filter((c) => c.activo !== false))
    setLoading(false)
  }, [temporadaId])

  useEffect(() => { load() }, [load])


  const openCreate = () => { setEditing(null); setForm(initialForm); setFormOpen(true) }
  const openEdit = async (p) => {
    const config = p.config || {}
    setEditing(p)
    setForm({
      nombre: p.nombre, tipo: p.tipo,
      porcentaje: p.porcentaje || '', monto_fijo: p.monto_fijo || '',
      descripcion: p.descripcion || '',
      config_hijo_2: config.hijo_2 || '', config_hijo_3: config.hijo_3 || '', config_hijo_4: config.hijo_4 || '',
      plan_id: p.plan_id || '', concepto_id: p.concepto_id || '',
    })
    setFormOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.nombre.trim()) return
    setSaving(true)

    const config = form.tipo === 'hermano' ? {
      ...(form.config_hijo_2 ? { hijo_2: Number(form.config_hijo_2) } : {}),
      ...(form.config_hijo_3 ? { hijo_3: Number(form.config_hijo_3) } : {}),
      ...(form.config_hijo_4 ? { hijo_4: Number(form.config_hijo_4) } : {}),
    } : {}

    const payload = {
      nombre: form.nombre,
      tipo: form.tipo,
      porcentaje: form.porcentaje ? Number(form.porcentaje) : null,
      monto_fijo: form.monto_fijo ? Number(form.monto_fijo) : null,
      config,
      descripcion: form.descripcion || null,
      temporada_id: temporadaId || null,
      plan_id: form.plan_id || null,
      concepto_id: form.concepto_id || null,
    }

    // Ya no se pregunta "¿recalculo tambien los que tienen pagos?": la regla lo
    // contesta sola. Sin pagos se abarata el cargo; con pagos no se toca y el
    // descuento llega como saldo a favor.
    const result = editing
      ? await actualizarPromocion(editing.id, payload)
      : await createPromocion(payload)
    setSaving(false)
    if (result.error) { await alert('Error', result.error.message || 'No se pudo guardar la promocion'); return }

    guard.resetDirty()
    setFormOpen(false)
    load()
    if (editing) await avisarResumen('Promoción actualizada', result.resumen)
  }

  // Que le pasa al dinero, en una linea. Sale del mismo resumen que devuelve el
  // motor, asi que no puede prometer algo distinto de lo que hizo.
  const avisarResumen = async (titulo, resumen) => {
    if (!resumen) return
    const parts = []
    if (resumen.cambios?.length) parts.push(`${resumen.cambios.length} cargo(s) actualizados`)
    if (resumen.historia?.length) parts.push(`${resumen.historia.length} con pagos no se modificaron`)
    if (resumen.saldoGenerado > 0) parts.push(`${formatMoney(resumen.saldoGenerado)} en saldo a favor`)
    if (parts.length) await alert(titulo, parts.join('. ') + '.')
  }

  const handleDelete = async (p) => {
    const { resumen: previo } = await desactivarPromocion(p.id, { dryRun: true })
    const efecto = previo?.cambios?.length
      ? `\n\n${previo.cambios.length} cargo(s) volverán a su monto original.`
      : ''
    if (!await confirm('Desactivar promocion', `Desactivar "${p.nombre}"?${efecto}`, { destructive: true })) return

    const { error, resumen } = await desactivarPromocion(p.id)
    if (error) { await alert('Error', error.message || 'No se pudo desactivar'); return }
    load()
    await avisarResumen('Promoción desactivada', resumen)
  }

  // Cuanta gente tiene esta promocion. Reemplaza a la etiqueta de "alcance":
  // ahora una promocion no se define por a que aplica sino por a quien se le dio.
  const asignados = (p) => p.pagos_promociones_jugadores?.[0]?.count ?? 0

  // La escalera que se esta capturando, con la forma en que se guarda, para que la
  // vista previa la lea con la funcion del calculo.
  const configEscalera = {
    ...(form.config_hijo_2 ? { hijo_2: Number(form.config_hijo_2) } : {}),
    ...(form.config_hijo_3 ? { hijo_3: Number(form.config_hijo_3) } : {}),
    ...(form.config_hijo_4 ? { hijo_4: Number(form.config_hijo_4) } : {}),
  }

  const renderDescuento = (p) => {
    if (p.tipo === 'hermano') {
      const config = p.config || {}
      const parts = []
      if (config.hijo_2) parts.push(`2do: ${config.hijo_2}%`)
      if (config.hijo_3) parts.push(`3ro: ${config.hijo_3}%`)
      if (config.hijo_4) parts.push(`4to: ${config.hijo_4}%`)
      return parts.join(', ') || '—'
    }
    if (p.porcentaje) return `${p.porcentaje}%`
    if (p.monto_fijo) return `$${Number(p.monto_fijo).toLocaleString('es-MX')}`
    return '—'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {temporadaSelector || <span />}
        <Button onClick={openCreate} aria-label="Nueva promoción" title="Nueva promoción">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nueva promoción</span>
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : promociones.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No hay promociones configuradas para esta temporada.
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* Movil: lista. El descuento de hermanos es una frase ("2do: 10%, 3ro:
              20%...") y en columna de tabla forzaba el ancho y empujaba las
              acciones fuera de la pantalla. */}
          <div className="sm:hidden divide-y px-4">
            {promociones.map((p) => (
              <div key={p.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{p.nombre}</p>
                  <p className="text-xs text-muted-foreground truncate capitalize">
                    {p.tipo} · {asignados(p)} asignada(s)
                  </p>
                  <p className="text-sm font-semibold mt-0.5">{renderDescuento(p)}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" title="Más acciones">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4" />
                      Editar promoción
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(p)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      Eliminar promoción
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>

          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Asignada a</TableHead>
                <TableHead className="hidden md:table-cell">Aplica a</TableHead>
                <TableHead>Descuento</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promociones.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">
                    {p.nombre}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">{p.tipo}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{asignados(p)}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                    {p.plan_id ? planes.find((pl) => pl.id === p.plan_id)?.nombre || 'Plan' : ''}
                    {p.plan_id && p.concepto_id ? ' · ' : ''}
                    {p.concepto_id ? conceptos.find((c) => c.id === p.concepto_id)?.nombre || 'Concepto' : ''}
                    {!p.plan_id && !p.concepto_id ? 'Todo' : ''}
                  </TableCell>
                  <TableCell className="text-sm">{renderDescuento(p)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openEdit(p)}>
                        <Pencil className="h-3.5 w-3.5 sm:mr-1.5" />
                        <span className="hidden sm:inline text-xs">Editar</span>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDelete(p)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar promoción
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={formOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Promocion' : 'Nueva Promocion'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Descuento hermanos" required />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, tipo: v })) }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <p className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                  Esta promoción solo aplica a quien se la asignes. Se asigna desde
                  <strong> Pagos → Asignaciones</strong>, marcando jugadores, familias o grupos.
                  Al asignarla se abaratan los cargos que aún no han recibido pagos; los que ya
                  los tienen no se tocan y el descuento llega como saldo a favor.
                </p>
              </div>
            </div>

            {/* Aplica a plan o concepto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aplica a plan</Label>
                <Select value={form.plan_id || 'todos'} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, plan_id: v === 'todos' ? '' : v })) }}>
                  <SelectTrigger><SelectValue placeholder="Todos los planes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los planes</SelectItem>
                    {planes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Aplica a concepto</Label>
                <Select value={form.concepto_id || 'todos'} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, concepto_id: v === 'todos' ? '' : v })) }}>
                  <SelectTrigger><SelectValue placeholder="Todos los conceptos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los conceptos</SelectItem>
                    {conceptos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.tipo === 'hermano' ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Descuento por hermano (%)</Label>
                {/* La confusion que hay que matar aqui: leer "Descuento 2 Hermanos"
                    como "para familias de dos" y crear una promocion por cada
                    tamaño de familia. Una sola cubre a todas. */}
                <p className="text-xs text-muted-foreground">
                  <strong>Una sola promoción cubre a todas las familias.</strong> No crees una
                  por cada tamaño: cada hijo recibe el porcentaje de su lugar. Un escalón
                  vacío hereda el de arriba.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">2do hijo</Label>
                    <Input type="number" min="0" max="100" value={form.config_hijo_2} onChange={(e) => setForm((p) => ({ ...p, config_hijo_2: e.target.value }))} placeholder="10" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">3er hijo</Label>
                    <Input type="number" min="0" max="100" value={form.config_hijo_3} onChange={(e) => setForm((p) => ({ ...p, config_hijo_3: e.target.value }))} placeholder="20" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">4to hijo</Label>
                    <Input type="number" min="0" max="100" value={form.config_hijo_4} onChange={(e) => setForm((p) => ({ ...p, config_hijo_4: e.target.value }))} placeholder="30" />
                  </div>
                </div>
                {/* Lo que va a cobrar, dicho por la MISMA funcion que lo cobra: asi
                    lo que se ve al configurar no puede discrepar del recibo. */}
                <p className="rounded-md bg-muted/50 p-3 text-xs">
                  <span className="text-muted-foreground">Así queda: </span>
                  <strong>1er hijo paga completo</strong>
                  {[2, 3, 4].map((n) => {
                    const pct = porcentajeHermano(configEscalera, n)
                    return (
                      <span key={n}>
                        {' · '}
                        {n === 2 ? '2do' : n === 3 ? '3ro' : '4to'}{' '}
                        <strong>{pct > 0 ? `${pct}% de descuento` : 'paga completo'}</strong>
                      </span>
                    )
                  })}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Porcentaje (%)</Label>
                    {/* Excluyentes: capturar los dos daba doble beneficio por un
                        camino y cero por el otro. La forma del monto es la que
                        decide si se rebaja el cargo o se otorga saldo. */}
                    <Input type="number" min="0" max="100" step="0.01" value={form.porcentaje} onChange={(e) => setForm((p) => ({ ...p, porcentaje: e.target.value, monto_fijo: e.target.value ? '' : p.monto_fijo }))} placeholder="10" />
                  </div>
                  <div className="space-y-2">
                    <Label>Monto fijo ($)</Label>
                    <Input type="number" min="0" step="0.01" value={form.monto_fijo} onChange={(e) => setForm((p) => ({ ...p, monto_fijo: e.target.value, porcentaje: e.target.value ? '' : p.porcentaje }))} placeholder="100.00" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Es uno <strong>o</strong> el otro. El <strong>porcentaje</strong> rebaja el cargo:
                  el recibo sale más barato. El <strong>monto fijo</strong> otorga saldo a favor,
                  que tú decides a qué cargos aplicar.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} placeholder="Notas sobre la promoción" rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  )
}

export default PromocionesTab